import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import useOnClickOutside from '../hooks/useOnClickOutside';
import ROUTING_RULES from '../queries/routing-rules.graphql';

const highlightString = (string, subString) => {
  if (!subString || !string) return string;
  const subStrings = string.split(new RegExp(`(${subString})`, 'gi'));
  return (
    <span>
      {subStrings.map((str, i) => (
        <span
          key={i}
          className={classNames({
            highlight: str.toLowerCase() === subString.toLowerCase()
          })}
        >
          {str}
        </span>
      ))}
    </span>
  );
};

const RoutingRuleSelector = ({ selectedRule, onSelectRule }) => {
  const { loading, data } = useQuery(ROUTING_RULES, {
    variables: { limit: 1000, offset: 0 }
  });

  const [searchText, setSearchText] = useState('');
  const [routingRules, setRoutingRules] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const dropdownContainerRef = useRef();

  useEffect(() => {
    if (data) {
      setRoutingRules(data.routingRules);
    }
  }, [data]);

  const filterRules = debounce(searchText => {
    searchText = searchText.toLowerCase();
    const filteredData = data.routingRules.filter(({ ruleName, ruleDescription, ruleCode }) => {
      ruleName = ruleName.toLowerCase();
      ruleDescription = ruleDescription.toLowerCase();
      ruleCode = ruleCode.toLowerCase();

      return ruleName.includes(searchText) || ruleDescription.includes(searchText) || ruleCode.includes(searchText);
    });
    setRoutingRules(filteredData);
  }, 400);

  const handleSearch = e => {
    const searchText = e.target.value;
    setSearchText(searchText);
    filterRules(searchText);
  };

  const onClose = () => {
    const fakeEvent = { target: { value: '' } };
    handleSearch(fakeEvent);
    setIsOpen(false);
  };

  const handleSelect = ruleCode => {
    onSelectRule(ruleCode);
    onClose();
  };

  const handleReset = e => {
    handleSelect('');
  };

  useOnClickOutside(dropdownContainerRef, onClose);

  const activeRule = useMemo(() => {
    return (data && data.routingRules.find(rr => rr.ruleCode === selectedRule)) || {};
  }, [selectedRule, data]);

  if (loading) return <div>Loading...</div>;

  return (
    <div className="flex items-center">
      <div className="search-wrapper" ref={dropdownContainerRef}>
        <span className={'search-icon mirror'}>⌕</span>
        {activeRule.ruleCode && (
          <span className={'close-icon'} onClick={handleReset}>
            &#x2715;
          </span>
        )}
        {activeRule.ruleCode && (
          <p className={'search-hint'}>
            <span className="ruleName" title={activeRule.ruleName}>
              {activeRule.ruleName}
            </span>
            {activeRule.ruleDescription && (
              <span className="ruleDescription" title={activeRule.ruleDescription}>
                {activeRule.ruleDescription}
              </span>
            )}
            <span>{`ID: ${activeRule.ruleCode}`}</span>
          </p>
        )}
        <input
          value={searchText}
          disabled={activeRule.ruleCode}
          placeholder={activeRule.ruleCode ? undefined : 'Search Routing Rules'}
          className="search"
          onChange={handleSearch}
          onFocus={() => {
            setIsOpen(true);
          }}
        />
        {isOpen && (
          <div className="search-results">
            {!isEmpty(routingRules) ? (
              routingRules.map(({ ruleName, ruleDescription, ruleCode }) => (
                <div
                  key={ruleCode}
                  className="item"
                  onClick={() => {
                    handleSelect(ruleCode);
                  }}
                >
                  <div className="item-header pb-2 ruleName">{highlightString(ruleName, searchText)}</div>
                  {ruleDescription && (
                    <p className="pb-2 ruleDescription">{highlightString(ruleDescription, searchText)}</p>
                  )}
                  <p>ID: {highlightString(ruleCode, searchText)}</p>
                </div>
              ))
            ) : (
              <div className="item">No Routing Rules</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoutingRuleSelector;
